import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Receipt, Plus, Trash, FileXls, FilePdf, X, Upload, Robot, SpinnerGap, FilePlus, Paperclip } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useSearch } from '../components/VerticalTabs';
import { ExcelImportButton } from '../components/shared/ExcelImportButton';
import ReceiptOcrButton from '../components/expenses/ReceiptOcrButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = [
  { value: 'fournitures', label: 'Fournitures de bureau' },
  { value: 'telecom', label: 'Télécom / Internet' },
  { value: 'assurance', label: 'Assurance' },
  { value: 'loyer', label: 'Loyer' },
  { value: 'energie', label: 'Énergie (Eau, Gaz, Électricité)' },
  { value: 'carburant', label: 'Carburant' },
  { value: 'entretien', label: 'Entretien véhicule' },
  { value: 'honoraires', label: 'Honoraires (comptable, avocat)' },
  { value: 'formation', label: 'Formation' },
  { value: 'repas', label: 'Repas / Restauration' },
  { value: 'deplacement', label: 'Déplacement' },
  { value: 'autre', label: 'Autre' }
];

const ExpensesPage = ({ embedded }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [showModal, setShowModal] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [formData, setFormData] = useState({
    category: 'autre',
    description: '',
    amount_ht: 0,
    tva_rate: 20,
    amount_ht_2: 0,
    tva_rate_2: 0,
    invoice_number: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    supplier: '',
    frequency: 'one_time',
    end_date: '',
    payment_method: ''
  });
  // Attachment to upload AFTER expense is created (the OCR'd or manually picked photo)
  const [pendingAttachment, setPendingAttachment] = useState(null);

  // Map Gemini OCR result -> form fields
  const applyOcrResult = (d) => {
    if (!d) return;
    const updates = {};
    if (d.supplier) updates.supplier = d.supplier;
    if (d.invoice_number) updates.invoice_number = d.invoice_number;
    if (d.date) updates.date = d.date;
    if (Array.isArray(d.tva_lines) && d.tva_lines.length > 0) {
      const l1 = d.tva_lines[0];
      if (l1?.amount_ht != null) updates.amount_ht = Number(l1.amount_ht) || 0;
      if (l1?.rate != null) updates.tva_rate = Number(l1.rate) || 20;
      if (d.tva_lines.length > 1) {
        const l2 = d.tva_lines[1];
        if (l2?.amount_ht != null) updates.amount_ht_2 = Number(l2.amount_ht) || 0;
        if (l2?.rate != null) updates.tva_rate_2 = Number(l2.rate) || 0;
      }
    } else if (d.amount_ht_total != null) {
      updates.amount_ht = Number(d.amount_ht_total) || 0;
    }
    if (d.categorie) updates.category = d.categorie;
    if (!formData.description) {
      const head = d.articles?.[0]?.label || d.supplier || '';
      if (head) updates.description = head;
    }
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const computedTVA = (formData.amount_ht || 0) * ((formData.tva_rate || 0) / 100);
  const computedTVA2 = (formData.amount_ht_2 || 0) * ((formData.tva_rate_2 || 0) / 100);
  const computedTTC = (formData.amount_ht || 0) + computedTVA + (formData.amount_ht_2 || 0) + computedTVA2;

  const handleAnalyzePDF = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Veuillez selectionner un fichier PDF');
      return;
    }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/api/invoice-analysis/analyze`, fd, { withCredentials: true });
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        const updates = {};
        if (d.montant_ht != null) updates.amount_ht = parseFloat(d.montant_ht) || 0;
        if (d.taux_tva != null) updates.tva_rate = parseFloat(d.taux_tva) || 20;
        if (d.fournisseur) updates.supplier = d.fournisseur;
        if (d.date_facture) updates.date = d.date_facture;
        if (d.numero_facture) updates.description = `Facture ${d.numero_facture}`;
        setFormData(prev => ({ ...prev, ...updates }));
        toast.success('Facture analysee avec succes');
      } else {
        toast.error(res.data.error || 'Erreur lors de l\'analyse');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur analyse PDF');
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchExpenses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/expenses?month=${selectedMonth}`, { withCredentials: true });
      setExpenses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, API_URL]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const created = await axios.post(`${API_URL}/api/expenses`, formData, { withCredentials: true });
      // Upload the receipt attachment (if any) on the freshly-created expense
      if (pendingAttachment && created.data?.id) {
        const fd = new FormData();
        fd.append('file', pendingAttachment);
        try {
          await axios.post(`${API_URL}/api/expenses/${created.data.id}/attachment`, fd, { withCredentials: true });
        } catch { toast.warning('Depense creee mais piece jointe non sauvegardee'); }
      }
      toast.success('Dépense ajoutée');
      setShowModal(false);
      setPendingAttachment(null);
      setFormData({
        category: 'autre',
        description: '',
        amount_ht: 0,
        tva_rate: 20,
        amount_ht_2: 0,
        tva_rate_2: 0,
        invoice_number: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        supplier: '',
        frequency: 'one_time',
        end_date: '',
        payment_method: ''
      });
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadAttachmentsPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/expenses/attachments-pdf?month=${selectedMonth}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pieces_jointes_${selectedMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aucune piece jointe pour ce mois');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, { withCredentials: true });
      toast.success('Dépense supprimée');
      fetchExpenses();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const exportExcel = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/expenses/export/excel?month=${selectedMonth}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `depenses_${selectedMonth}.xlsx`;
      link.click();
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur export');
    }
  };

  const getCategoryLabel = (value) => EXPENSE_CATEGORIES.find(c => c.value === value)?.label || value;

  const { searchTerm } = useSearch();
  const filteredExpenses = expenses.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (e.description || '').toLowerCase().includes(s) || (e.supplier || '').toLowerCase().includes(s) || (e.project_name || '').toLowerCase().includes(s) || getCategoryLabel(e.category).toLowerCase().includes(s);
  });
  const totalHT = filteredExpenses.reduce((sum, e) => sum + e.amount_ht, 0);
  const totalTVA = filteredExpenses.reduce((sum, e) => sum + e.amount_tva, 0);
  const totalTTC = filteredExpenses.reduce((sum, e) => sum + e.amount_ttc, 0);

  // Generate months for selector
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(format(d, 'yyyy-MM'));
  }

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="expenses-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Dépenses & Notes de frais</h1>
          <p className="page-subtitle">Gérez vos dépenses mensuelles</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button className="btn btn-secondary" onClick={exportExcel} data-testid="export-expenses-excel-btn">
            <FileXls size={18} /> Excel
          </button>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/expenses/export/pdf?month=${selectedMonth}`, '_blank')} data-testid="export-expenses-pdf-btn">
            <FilePdf size={18} /> PDF
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadAttachmentsPdf} data-testid="download-attachments-btn" title="Telecharger toutes les pieces jointes du mois en 1 PDF">
            <Paperclip size={18} /> Pieces jointes
          </button>
          <ExcelImportButton kind="depenses" label="Importer" testid="expenses-" onImported={() => fetchExpenses()} />
          <button data-testid="add-expense-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Nouvelle dépense
          </button>
        </div>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '16px' }}>
          <button className="btn btn-secondary" onClick={exportExcel} data-testid="export-expenses-excel-btn">
            <FileXls size={18} /> Excel
          </button>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/expenses/export/pdf?month=${selectedMonth}`, '_blank')} data-testid="export-expenses-pdf-btn">
            <FilePdf size={18} /> PDF
          </button>
          <button className="btn btn-secondary" onClick={handleDownloadAttachmentsPdf} data-testid="download-attachments-emb-btn" title="Telecharger toutes les pieces jointes du mois en 1 PDF">
            <Paperclip size={18} /> Pieces jointes
          </button>
          <ExcelImportButton kind="depenses" label="Importer" testid="expenses-emb-" onImported={() => fetchExpenses()} />
          <button data-testid="add-expense-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} /> Nouvelle dépense
          </button>
        </div>
      )}

      {/* Month Selector & Summary */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span className="label-text">Mois :</span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger style={{ width: '180px' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: '24px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Total HT</div>
          <div className="stat-value" style={{ fontSize: '1.5rem' }}>{totalHT.toFixed(2)}€</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total TVA</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: '#EA580C' }}>{totalTVA.toFixed(2)}€</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total TTC</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: '#002FA7' }}>{totalTTC.toFixed(2)}€</div>
        </div>
      </div>

      {/* Expenses Table */}
      {filteredExpenses.length > 0 ? (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Catégorie</th>
                <th>Description</th>
                <th>Fournisseur</th>
                <th>HT</th>
                <th>TVA %</th>
                <th>TVA</th>
                <th>TTC</th>
                <th>Piece jointe</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((expense) => (
                <tr key={expense.id}>
                  <td>{expense.date}</td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#F4F5F7',
                      borderRadius: '2px',
                      fontSize: '0.75rem'
                    }}>
                      {getCategoryLabel(expense.category)}
                    </span>
                  </td>
                  <td>{expense.description}</td>
                  <td>{expense.supplier || '-'}</td>
                  <td>{expense.amount_ht.toFixed(2)}€</td>
                  <td>{expense.tva_rate}%</td>
                  <td>{expense.amount_tva.toFixed(2)}€</td>
                  <td style={{ fontWeight: '600' }}>{expense.amount_ttc.toFixed(2)}€</td>
                  <td>
                    {expense.attachment?.url ? (
                      <a
                        href={`${API_URL}${expense.attachment.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary btn-sm"
                        title={expense.attachment.original_name || 'Piece jointe'}
                        data-testid={`expense-attachment-${expense.id}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      >
                        <Paperclip size={14} /> Voir
                      </a>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontSize: '0.75rem' }}>-</span>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDelete(expense.id)}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div style={{ padding: '60px', textAlign: 'center', color: '#5E5F66' }}>
            <Receipt size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>Aucune dépense pour ce mois</p>
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showModal && (
        <div className="form-modal-overlay">
          <div className="form-modal-card">
            <div className="form-modal-header">
              <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: '1.05rem' }}>Nouvelle dépense</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
            </div>
            <form onSubmit={handleSubmit} className="form-modal-body">
              {/* OCR Photo with Gemini */}
              <div className="card" style={{ padding: '14px', marginBottom: '16px', backgroundColor: '#F4F8FF', border: '1px dashed #1D4ED8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Robot size={18} weight="duotone" style={{ color: '#1D4ED8' }} />
                  <span style={{ fontWeight: 700, fontSize: '0.86rem', color: '#1D4ED8' }}>Auto-remplir depuis une photo de facture</span>
                </div>
                <ReceiptOcrButton
                  endpoint="/api/expenses/ocr"
                  onResult={applyOcrResult}
                  onFilePicked={setPendingAttachment}
                />
                <div style={{ fontSize: '0.74rem', color: '#5E5F66', marginTop: 6 }}>
                  La photo sera enregistree comme piece jointe et restera telechargeable.
                </div>
              </div>

              {/* PDF Analysis (legacy) */}
              <div className="card" style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#F8F9FF', border: '1px dashed #90CAF9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Robot size={16} weight="duotone" style={{ color: '#1565C0' }} />
                  <span style={{ fontWeight: '600', fontSize: '0.84rem', color: '#1565C0' }}>Ou : analyser une facture PDF</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: analyzing ? 0.5 : 1 }}>
                    {analyzing ? <SpinnerGap size={14} className="spin" /> : <Upload size={14} />}
                    {analyzing ? 'Analyse en cours...' : 'Importer une facture PDF'}
                    <input type="file" accept=".pdf" style={{ display: 'none' }} disabled={analyzing}
                      onChange={(e) => { if (e.target.files[0]) handleAnalyzePDF(e.target.files[0]); e.target.value = ''; }}
                      data-testid="analyze-pdf-input" />
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Categorie *</label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <label className="form-label">Description *</label>
                <input type="text" className="form-input" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Fournisseur</label>
                  <input type="text" className="form-input" value={formData.supplier} onChange={(e) => setFormData({...formData, supplier: e.target.value})} placeholder="Optionnel" />
                </div>
                <div className="form-group">
                  <label className="form-label">N° facture</label>
                  <input type="text" className="form-input" value={formData.invoice_number} onChange={(e) => setFormData({...formData, invoice_number: e.target.value})} placeholder="Optionnel" data-testid="expense-invoice-number" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Base HT TVA 1 *</label>
                  <input type="number" step="0.01" className="form-input" value={formData.amount_ht} onChange={(e) => setFormData({...formData, amount_ht: parseFloat(e.target.value) || 0})} required data-testid="expense-amount-ht" />
                </div>
                <div className="form-group">
                  <label className="form-label">Taux TVA 1 %</label>
                  <input type="number" step="0.1" className="form-input" value={formData.tva_rate} onChange={(e) => setFormData({...formData, tva_rate: parseFloat(e.target.value) || 20})} data-testid="expense-tva-rate" />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.82rem', color: '#5E5F66' }}>Base HT TVA 2 (facultatif)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.amount_ht_2 || ''} onChange={(e) => setFormData({...formData, amount_ht_2: parseFloat(e.target.value) || 0})} data-testid="expense-amount-ht-2" placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '0.82rem', color: '#5E5F66' }}>Taux TVA 2 %</label>
                  <input type="number" step="0.1" className="form-input" value={formData.tva_rate_2 || ''} onChange={(e) => setFormData({...formData, tva_rate_2: parseFloat(e.target.value) || 0})} data-testid="expense-tva-rate-2" placeholder="0" />
                </div>
              </div>
              {(formData.amount_ht > 0 || formData.amount_ht_2 > 0) && (
                <div data-testid="expense-ttc-preview" style={{ padding: '12px 16px', backgroundColor: '#E8F5E9', borderRadius: '6px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#2E7D32', display: 'flex', justifyContent: 'space-between' }}>
                    <span>TVA 1 ({formData.tva_rate}%): <strong>{computedTVA.toFixed(2)} EUR</strong></span>
                    {formData.amount_ht_2 > 0 && <span>TVA 2 ({formData.tva_rate_2}%): <strong>{computedTVA2.toFixed(2)} EUR</strong></span>}
                  </div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, color: '#1B5E20', marginTop: 4 }}>
                    Total TTC : {computedTTC.toFixed(2)} EUR
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input type="date" className="form-input" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Frequence</label>
                  <select className="form-input" value={formData.frequency} onChange={(e) => setFormData({...formData, frequency: e.target.value})} data-testid="expense-frequency">
                    <option value="one_time">Ponctuelle</option>
                    <option value="monthly">Mensuelle (recurrente)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Mode de paiement</label>
                  <select className="form-input" value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})} data-testid="expense-payment-method">
                    <option value="">Non specifie</option>
                    <option value="prelevement">Prelevement</option>
                    <option value="cb">Carte bancaire</option>
                    <option value="virement">Virement</option>
                    <option value="especes">Especes</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              </div>
              {formData.frequency === 'monthly' && (
                <div className="form-group">
                  <label className="form-label">Date de fin de la recurrence <span style={{ color: '#C62828' }}>*</span></label>
                  <input type="date" className="form-input" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required={formData.frequency === 'monthly'} data-testid="expense-end-date" />
                  <small style={{ color: '#5E5F66', fontSize: '0.75rem' }}>La depense sera generee automatiquement chaque mois jusqu'a cette date.</small>
                </div>
              )}
            </form>
            <div className="form-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Annuler</button>
              <button type="button" className="btn btn-primary" onClick={handleSubmit} style={{ flex: 1 }} data-testid="submit-expense-btn">Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
